import React, { useState } from "react";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Typography,
  Tooltip,
  AppBar,
  Toolbar,
  useTheme,
  useMediaQuery,
  SwipeableDrawer,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  Dashboard,
  Business,
  Group,
  Article,
  Help,
  ChevronLeft,
  ChevronRight,
  Menu as MenuIcon,
  Category,
  QueryStats,
  Leaderboard,
  DiscountOutlined,
  BrandingWatermark,
  QuestionAnswer,
  ContactPage,
  Notifications,
} from "@mui/icons-material";
import {
  BadgeIndianRupee,
  BookOpenText,
  Cog,
  Handshake,
  LanguagesIcon,
  Radius,
  TicketX,
  Timer,
  Truck,
  User,
  User2,
  Users,
  Warehouse,
} from "lucide-react";

import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { useNavigate, useLocation } from "react-router-dom";
import PageRoutes from "../routes/PageRoutes";
import PageSideRoute from "../routes/PageSideRoute";
import ViewCarouselRoundedIcon from "@mui/icons-material/ViewCarouselRounded";

const drawerWidth = 280;

const menuItems = [
  {
    label: "Dashboard",
    icon: <Dashboard />,
    link: "/display-dashboard",
  },
  {
    label: "Management",
    icon: <Group />,
    children: [
      { label: "User Management", link: "/display-users" },
      { label: "Technician List", link: "/display-technicians" },
      { label: "Showroom List", link: "/display-showrooms" },
      { label: "Technician Payouts ", link: "/technician-payouts" },
      { label: "Showroom Payouts", link: "/showroom-payouts" },
    ],
  },
  {
    label: "Roles Management",
    icon: <User />,
    children: [
      { label: "Employee Directory", link: "/display-employees" },
      { label: "Manage Roles", link: "/display-roles" },
      { label: "Manage Permissions", link: "/display-permissions" },
    ],
  },
  {
    label: "Booking Management",
    icon: <AccountCircleIcon />,
    link: "/display-bookings",
  },
  {
    label: "Service Catalog",
    icon: <Category />,
    children: [
      { label: "Core Services", link: "/display-service" },
      { label: "Add-on Services", link: "/display-addon-service" },
      { label: "Package Services", link: "/display-package-service" },
    ],
  },
  {
    label: "Offer Management",
    icon: <DiscountOutlined />,
    link: "/display-offers"
  },
  {
    label: "Vehicle Brands & Models",
    icon: <BrandingWatermark />,
    children: [
      { label: "Brand List", link: "/display-vehicle-brands" },
      // { label: 'Add Vehicle Brands', link: 'create-edit-vehicle-brand' },
      { label: "Model List", link: "/display-vehicle-models" },
      // { label: 'Add Vehicle Model', link: 'create-edit-vehicle-model' }
    ],
  },
  {
    label: "Delivery Management",
    icon: <Truck />,
    children: [
      { label: "Delivery Range Settings", link: "/display-deliveryrange" },
      { label: "Delivery Radius Settings", link: "/display-delivery-radius" },
    ],
  },
  {
    label: "Time Slot Settings",
    icon: <Timer />,
    link: "/display-timeslots",
  },
  {
    label: "Push Notifications",
    icon: <Notifications />,
    link: "/push-notifications"
  },
  {
    label: "Cancellation Policy",
    icon: <TicketX />,
    link: "/display-cancellation-policy",
  },
  {
    label: "Referral Program",
    icon: <Cog />,
    link: "/display-referal",
  },
  {
    label: "Banner Management",
    icon: <ViewCarouselRoundedIcon />,
    link: "/display-banners",
  },

  {
    label: "Commission & Taxes",
    icon: <BadgeIndianRupee />,
    link: "/display-taxfee",
  },
  {
    label: "Customer Enquiries",
    icon: <QuestionAnswer />,
    link: "/display-enquiry",
  }, {
    label: "CMS",
    icon: <Help />,
    children: [
      { label: "FAQs", link: "/display-faqs" },
      { label: "Terms & Privacy Policy", link: "/display-terms-privacies" }
    ],
  },
];

const SidebarItem = ({
  item,
  openSidebar,
  depth = 0,
  currentPath,
  expandedItems,
  setExpandedItems,
  onItemClick,
}) => {
  const hasChildren = Array.isArray(item.children);
  const isExpanded = expandedItems.includes(item.label);

  const navigate = useNavigate();

  // Check if the current path matches this item or any of its children
  const isSelected =
    item.link === currentPath ||
    (hasChildren && item.children.some((child) => child.link === currentPath));

  const handleClick = () => {
    if (hasChildren) {
      setExpandedItems((prev) => {
        if (depth === 0) {
          return isExpanded ? [] : [item.label];
        } else {
          return isExpanded
            ? prev.filter((label) => label !== item.label)
            : [...prev, item.label];
        }
      });
    }

    if (item.link) {
      navigate(item.link);
    }

    if (onItemClick && !hasChildren) onItemClick();
  };

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Box>
          <ListItem
            button
            onClick={handleClick}
            sx={{
              backgroundColor: isSelected ? "red" : "transparent",
              "&:hover": {
                backgroundColor: isSelected
                  ? "darkred"
                  : "rgba(255, 255, 255, 0.1)",
              },
              pl: 2 + depth * 1.5,
            }}
          >
            <Tooltip title={!openSidebar ? item.label : ""} placement="right">
              <ListItemIcon sx={{ color: "#fff", minWidth: 40, py: 1 }}>
                {item.icon || <Box sx={{ width: 18 }} />}
              </ListItemIcon>
            </Tooltip>

            {openSidebar && (
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  color: "#fff",
                  fontSize: depth === 0 ? "14px" : "0.85rem",
                  fontWeight: depth === 0 ? 500 : 400,
                }}
              />
            )}

            {hasChildren &&
              openSidebar &&
              (isExpanded ? <ExpandLess /> : <ExpandMore />)}
          </ListItem>

          {hasChildren && (
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <List disablePadding>
                {item.children.map((child, index) => (
                  <SidebarItem
                    key={index}
                    item={child}
                    openSidebar={openSidebar}
                    depth={depth + 1}
                    currentPath={currentPath}
                    expandedItems={expandedItems}
                    setExpandedItems={setExpandedItems}
                    onItemClick={onItemClick}
                  />
                ))}
              </List>
            </Collapse>
          )}
        </Box>
      </Box>
    </>
  );
};

const SidebarContent = ({
  openSidebar,
  currentPath,
  expandedItems,
  setExpandedItems,
  onItemClick,
}) => (
  <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
    {/* Header */}
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: openSidebar ? "space-between" : "center",
        p: 2,
        minHeight: { sm: "auto", md: 64 },
        bgcolor: "#111",
        borderBottom: "1px solid #333",
      }}
    >
      {openSidebar ? (
        <Typography variant="h6" fontWeight="bold" color="#fff">
          Xpress Wheel
        </Typography>
      ) : (
        <Typography
          variant="h6"
          fontWeight="bold"
          color="red"
          paddingLeft={"28px"}
        >
          I
        </Typography>
      )}
    </Box>
    {/* Menu Items */}
    <List
      disablePadding
      sx={{ overflowY: "scroll", scrollbarWidth: "none", cursor: "pointer" }}
    >
      {menuItems.map((item, index) => (
        <SidebarItem
          key={index}
          item={item}
          openSidebar={openSidebar}
          currentPath={currentPath}
          expandedItems={expandedItems}
          setExpandedItems={setExpandedItems}
          onItemClick={onItemClick}
        />
      ))}
    </List>
  </Box>
);

const Sidebar = () => {
  const [openSidebar, setOpenSidebar] = useState(true);
  const [expandedItems, setExpandedItems] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const currentPath = location.pathname;

  // Drawer content for both desktop and mobile
  const drawerContent = (
    <SidebarContent
      openSidebar={openSidebar}
      currentPath={currentPath}
      expandedItems={expandedItems}
      setExpandedItems={setExpandedItems}
      onItemClick={() => {
        if (isMobile) setMobileOpen(false);
      }}
    />
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Sidebar and Navbar */}
      <>
        {isMobile && (
          <AppBar
            position="fixed"
            sx={{ zIndex: theme.zIndex.drawer + 1, bgcolor: "#111" }}
          >
            <Toolbar>
              <IconButton
                color="inherit"
                edge="start"
                onClick={() => setMobileOpen((prev) => !prev)}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
              <Typography variant="h6" noWrap component="div">
                Xpress Wheel
              </Typography>
            </Toolbar>
          </AppBar>
        )}

        {/* Desktop Drawer */}
        {!isMobile && (
          <Drawer
            variant="permanent"
            open={openSidebar}
            sx={{
              width: openSidebar ? drawerWidth : 72,
              flexShrink: 0,
              "& .MuiDrawer-paper": {
                width: openSidebar ? drawerWidth : 72,
                boxSizing: "border-box",
                backgroundColor: "#111",
                color: "#fff",
                overflow: "hidden",
              },
            }}
          >
            {/* Header with collapse button */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: openSidebar ? "space-between" : "center",
                p: 2,
                minHeight: 64,
                bgcolor: "#111",
                borderBottom: "1px solid #333",
              }}
            >
              {openSidebar ? (
                <Typography variant="h6" fontWeight="bold" color="#fff">
                  Xpress Wheel
                </Typography>
              ) : (
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color="red"
                  paddingLeft={"28px"}
                >
                  I
                </Typography>
              )}

              <IconButton
                onClick={() => setOpenSidebar(!openSidebar)}
                sx={{ color: "#fff", ml: openSidebar ? 0 : "auto" }}
              >
                {openSidebar ? <ChevronLeft /> : <ChevronRight />}
              </IconButton>
            </Box>
            {/* Menu Items */}
            <List
              disablePadding
              sx={{
                overflowY: "scroll",
                scrollbarWidth: "none",
                cursor: "pointer",
              }}
            >
              {menuItems.map((item, index) => (
                <SidebarItem
                  key={index}
                  item={item}
                  openSidebar={openSidebar}
                  currentPath={currentPath}
                  expandedItems={expandedItems}
                  setExpandedItems={setExpandedItems}
                />
              ))}
            </List>
          </Drawer>
        )}

        {/* Mobile Drawer */}
        {isMobile && (
          <SwipeableDrawer
            anchor="left"
            open={mobileOpen}
            onOpen={() => setMobileOpen(true)}
            onClose={() => setMobileOpen(false)}
            sx={{
              "& .MuiDrawer-paper": {
                width: drawerWidth,
                backgroundColor: "#111",
                color: "#fff",
              },
            }}
          >
            {drawerContent}
          </SwipeableDrawer>
        )}
      </>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: "100%",
          mt: isMobile ? "64px" : 0,
          transition: "margin 0.3s",
        }}
      >
        <PageRoutes />
      </Box>
    </Box>
  );
};
export default Sidebar;
